// src/components/chat/GlobalChatPanel.tsx
/**
 * GlobalChatPanel Component
 * Slide-out panel for the global AI chat assistant.
 * Shows "Knowledge Base Assistant" header and KB context banner in RAG mode.
 */

import { memo, useRef, useEffect, useState } from 'react';
import { X, MessageSquare, Trash2, BookOpen, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { useGlobalChat } from '@/contexts/globalChatContext';
import { ChatMessageMemo } from './ChatMessage';
import { ChatInputMemo } from './ChatInput';
import type { IGlobalChatPanelProps } from '@/@types/globalChat';

function GlobalChatPanel({ className }: IGlobalChatPanelProps) {
  const {
    messages,
    status,
    isOpen,
    currentContext,
    isKnowledgeBaseMode,
    sendMessage,
    closeChat,
    clearMessages,
    deleteMessages,
  } = useGlobalChat();

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isLoading = status === 'sending' || status === 'streaming';

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  const inputPlaceholder = isKnowledgeBaseMode
    ? 'Ask the knowledge base...'
    : currentContext
      ? `Ask about this ${currentContext.type}...`
      : 'Type your message...';

  return (
    <aside
      role="complementary"
      aria-label="AI Assistant chat panel"
      aria-hidden={!isOpen}
      className={cn(
        'fixed right-0 top-0 h-full w-[400px] bg-background border-l shadow-lg z-50',
        'transform transition-transform duration-300 ease-in-out flex flex-col',
        isOpen ? 'translate-x-0' : 'translate-x-full',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
        <div className="flex items-center gap-2">
          {isKnowledgeBaseMode ? (
            <BookOpen className="h-5 w-5 text-foreground" />
          ) : (
            <MessageSquare className="h-5 w-5 text-foreground" />
          )}
          <h2 className="font-semibold">
            {isKnowledgeBaseMode ? 'Knowledge Base Assistant' : 'AI Assistant'}
          </h2>
        </div>
        <div className="flex items-center gap-1">
          {messages.length > 0 && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsDeleteDialogOpen(true)}
              aria-label="Clear chat history"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          <Button variant="ghost" size="icon" onClick={closeChat} aria-label="Close chat panel">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Knowledge Base Context Banner */}
      {isKnowledgeBaseMode && (
        <div className="px-4 py-2 bg-primary/10 border-b text-sm flex-shrink-0 flex items-center gap-2">
          <BookOpen className="h-3.5 w-3.5 text-foreground flex-shrink-0" />
          <span className="text-muted-foreground">
            Searching your knowledge base for accurate answers
          </span>
        </div>
      )}

      {/* Standard Context Banner */}
      {!isKnowledgeBaseMode && currentContext && (
        <div className="px-4 py-2 bg-muted/50 border-b text-sm flex-shrink-0">
          <span className="text-muted-foreground">Context: </span>
          <span className="font-medium capitalize">{currentContext.type}</span>
          {currentContext.entityType && (
            <span className="text-muted-foreground"> ({currentContext.entityType})</span>
          )}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full min-h-[300px] text-center p-6">
            {isKnowledgeBaseMode ? (
              <>
                <BookOpen className="h-12 w-12 text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground">Knowledge base ready.</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Ask questions about your insurance or real estate knowledge base.
                </p>
              </>
            ) : (
              <>
                <MessageSquare className="h-12 w-12 text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground">No messages yet.</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Start a conversation with your AI assistant.
                </p>
              </>
            )}
          </div>
        ) : (
          <div className="py-2">
            {messages.map(message => (
              <ChatMessageMemo key={message.id} message={message} />
            ))}

            {/* Loading indicator while AI is thinking */}
            {isLoading && (
              <div className="flex items-start gap-3 px-4 py-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                  <BookOpen className="w-4 h-4 text-muted-foreground" />
                </div>
                <div className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Thinking...</span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input */}
      <div className="flex-shrink-0">
        <ChatInputMemo onSend={sendMessage} isDisabled={isLoading} placeholder={inputPlaceholder} />
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Clear chat history?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isKnowledgeBaseMode
                ? 'This will permanently delete all messages in this conversation, including saved history.'
                : 'This will clear all messages in the current conversation.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                if (isKnowledgeBaseMode) {
                  await deleteMessages();
                } else {
                  clearMessages();
                }
              }}
            >
              Clear
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </aside>
  );
}

const GlobalChatPanelMemo = memo(GlobalChatPanel);

export { GlobalChatPanelMemo };
